import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import { getLeadsEnGestion, getLeadEstadoSelect, editarLeadEstado } from '../api/apiConfig';
import { MdReceipt, MdArrowForward } from 'react-icons/md';
import { FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';
import { IoSearchOutline, IoClose } from 'react-icons/io5';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';
import * as XLSX from 'xlsx';

const EnGestion = () => {
  const navigate = useNavigate();
  const { user } = useUser();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [searchTerm, setSearchTerm] = useState('');
  const itemsPerPage = 15;
  const [estadoOptions, setEstadoOptions] = useState([]);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const filterData = (dataToFilter) => {
    if (!searchTerm.trim()) return dataToFilter;
    const searchLower = searchTerm.toLowerCase().trim();
    return dataToFilter.filter(item => {
      const searchableFields = [item.RUT_LEAD, item.NombreORazonSocial, item.EJEC_ASIG, item.EQUIPO, item.ORIGEN, item.FECHA_ASIG, item.Telefonos, item.Email, item.TamanoEmpresaDescripcion, item.ESTADO, item.EJEC_LINEA];
      return searchableFields.some(field => field != null && String(field).toLowerCase().includes(searchLower));
    });
  };

  /** Leads cargados masivamente sobre un cliente con línea: la asignación manda, pero conviene ver quién la tiene. */
  const ejecutivoLineaDistinto = (lead) => {
    const linea = String(lead?.EJEC_LINEA || '').trim();
    if (!linea) return null;
    const asignado = String(lead?.EJEC_ASIG || '').trim();
    return linea.toLowerCase() === asignado.toLowerCase() ? null : linea;
  };

  const getSortedData = () => {
    let dataToSort = filterData(leads);
    if (!sortColumn) return dataToSort;
    return [...dataToSort].sort((a, b) => {
      let aValue = a[sortColumn]; let bValue = b[sortColumn];
      if (aValue == null) aValue = ''; if (bValue == null) bValue = '';
      const aNum = typeof aValue === 'number' ? aValue : parseFloat(aValue);
      const bNum = typeof bValue === 'number' ? bValue : parseFloat(bValue);
      const isNumeric = !isNaN(aNum) && !isNaN(bNum) && isFinite(aNum) && isFinite(bNum);
      const comparison = isNumeric ? aNum - bNum : String(aValue).toLowerCase().localeCompare(String(bValue).toLowerCase(), 'es', { numeric: true, sensitivity: 'base' });
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  };

  const sortedData = getSortedData();
  const totalPages = Math.ceil(sortedData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentData = sortedData.slice(startIndex, endIndex);

  const handleSort = (column) => {
    if (sortColumn === column) setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    else { setSortColumn(column); setSortDirection('asc'); }
    setCurrentPage(1);
  };

  const handleSearchChange = (e) => { setSearchTerm(e.target.value); setCurrentPage(1); };
  const clearSearch = () => { setSearchTerm(''); setCurrentPage(1); };

  const getSortIcon = (column) => {
    if (sortColumn !== column) return <FaSort style={{ marginLeft: '4px', opacity: 0.3 }} />;
    return sortDirection === 'asc' ? <FaSortUp style={{ marginLeft: '4px' }} /> : <FaSortDown style={{ marginLeft: '4px' }} />;
  };

  const goToPage = (page) => {
    setCurrentPage(page);
  };

  const getPageNumbers = () => {
    const maxButtons = 7;
    if (totalPages <= maxButtons) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    
    const half = Math.floor(maxButtons / 2);
    let start = Math.max(1, currentPage - half);
    let end = Math.min(totalPages, start + maxButtons - 1);
    
    if (end - start + 1 < maxButtons) {
      start = Math.max(1, end - maxButtons + 1);
    }
    
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  const handleEstadoChange = async (rutLead, nuevoEstado) => {
    try {
      const estadoOption = estadoOptions.find(opt => opt.NOM_COD_CORTO === nuevoEstado);
      if (estadoOption) {
        await editarLeadEstado(rutLead, estadoOption.ID_COD);
        // Actualizar el estado local
        setLeads(prevLeads => 
          prevLeads.map(lead => 
            lead.RUT_LEAD === rutLead ? { ...lead, ESTADO: nuevoEstado } : lead
          )
        );
      }
    } catch (error) {
      console.error('Error updating lead status:', error);
    }
  };

  const exportToExcel = () => {
    const dataToExport = getSortedData();
    const data = dataToExport.map(lead => ({
      'RUT': lead.RUT_LEAD || '',
      'Razón Social': lead.NombreORazonSocial || '',
      'Ejecutivo': lead.EJEC_ASIG || '',
      'Ejecutivo en líneas': lead.EJEC_LINEA || '',
      'Equipo': lead.EQUIPO || '',
      'Origen': lead.ORIGEN || '',
      'Fecha Asignación': lead.FECHA_ASIG || '',
      'Teléfono': lead.Telefonos || '',
      'Email': lead.Email || '',
      'Tamaño Empresa': lead.TamanoEmpresaDescripcion || '',
      'Estado': lead.ESTADO || ''
    }));
    
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Leads En Gestión');
    
    XLSX.writeFile(wb, `leads_en_gestion_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        // Cargar leads
        const result = await getLeadsEnGestion(user);
        let data = [];
        if (result.body && typeof result.body === 'string') {
          data = JSON.parse(result.body);
        } else if (Array.isArray(result)) {
          data = result;
        }
        setLeads(data);
        
        // Cargar opciones de estado
        const estadoResult = await getLeadEstadoSelect();
        let estadoData = [];
        if (estadoResult.body && typeof estadoResult.body === 'string') {
          estadoData = JSON.parse(estadoResult.body);
        } else if (Array.isArray(estadoResult)) {
          estadoData = estadoResult;
        }
        setEstadoOptions(estadoData);

      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      loadData();
    }
  }, [user]);

  return (
    <div className="panel-container">
      <div className="flex items-baseline gap-4 mb-6">
      </div>
      
      <div className="seccion-datos">
        <div className="flex justify-between items-center mb-4">
          <h3 style={{color: 'var(--if-primary)', margin: '0 0 1rem 0', fontSize: '1.2rem', fontWeight: '600'}}>
            Leads en Gestión
            <span className="text-sm ml-2" style={{color: 'var(--if-text-muted)'}}>
              ({leads.length} registros)
            </span>
          </h3>
          {leads.length > 0 && (
            <button
              onClick={exportToExcel}
              className="btn-primary"
              style={{
                background: 'linear-gradient(135deg, var(--if-success) 0%, #16a34a 100%)',
                padding: '0.5rem 1rem',
                fontSize: '0.875rem',
                borderLeft: '3px solid var(--if-primary)'
              }}
            >
              <MdReceipt className="w-4 h-4 mr-2" style={{display: 'inline'}} />
              Exportar Excel
            </button>
          )}
        </div>
        {leads.length > 0 && (
          <div className="mb-4">
            <div className="relative" style={{ maxWidth: '500px' }}>
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <IoSearchOutline className="h-5 w-5" style={{ color: '#9ca3af' }} />
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={handleSearchChange}
                placeholder="Buscar en todas las columnas..."
                className="w-full pl-10 pr-10 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {searchTerm && (
                <button type="button" onClick={clearSearch} className="absolute inset-y-0 right-0 pr-3 flex items-center" style={{ color: '#9ca3af' }} title="Limpiar búsqueda">
                  <IoClose className="h-5 w-5" />
                </button>
              )}
            </div>
            {searchTerm && (
              <div className="mt-2 text-sm" style={{ color: 'var(--if-text-muted)' }}>
                {sortedData.length === 0 ? <span>No se encontraron resultados para &quot;{searchTerm}&quot;</span> : <span>{sortedData.length} resultado{sortedData.length !== 1 ? 's' : ''} encontrado{sortedData.length !== 1 ? 's' : ''}{sortedData.length !== leads.length ? ` de ${leads.length} registros` : ''}</span>}
              </div>
            )}
          </div>
        )}

        {loading ? (
          <div className="flex justify-center items-center h-32">
            <AiOutlineLoading3Quarters className="animate-spin h-8 w-8" style={{color: 'var(--if-primary)'}} />
          </div>
        ) : leads.length === 0 ? (
          <div className="text-center py-8" style={{color: 'var(--if-text-muted)'}}>
            No hay leads en gestión
          </div>
        ) : sortedData.length === 0 && searchTerm ? (
          <div className="text-center py-8" style={{color: 'var(--if-text-muted)'}}>No se encontraron resultados para &quot;{searchTerm}&quot;</div>
        ) : (
          <div className="tabla-container" style={{overflowX: 'auto', maxHeight: '600px'}}>
            <table>
              <thead>
                <tr>
                  <th style={{minWidth: '100px', cursor: 'pointer', userSelect: 'none'}} onClick={() => handleSort('RUT_LEAD')}>RUT {getSortIcon('RUT_LEAD')}</th>
                  <th style={{minWidth: '300px', cursor: 'pointer', userSelect: 'none'}} onClick={() => handleSort('NombreORazonSocial')}>Razón Social {getSortIcon('NombreORazonSocial')}</th>
                  <th style={{minWidth: '200px', cursor: 'pointer', userSelect: 'none'}} onClick={() => handleSort('EJEC_ASIG')}>Ejecutivo {getSortIcon('EJEC_ASIG')}</th>
                  <th style={{cursor: 'pointer', userSelect: 'none'}} onClick={() => handleSort('EQUIPO')}>Equipo {getSortIcon('EQUIPO')}</th>
                  <th style={{cursor: 'pointer', userSelect: 'none'}} onClick={() => handleSort('ORIGEN')}>Origen {getSortIcon('ORIGEN')}</th>
                  <th style={{cursor: 'pointer', userSelect: 'none'}} onClick={() => handleSort('FECHA_ASIG')}>Fecha Asignación {getSortIcon('FECHA_ASIG')}</th>
                  <th style={{cursor: 'pointer', userSelect: 'none'}} onClick={() => handleSort('Telefonos')}>Teléfono {getSortIcon('Telefonos')}</th>
                  <th style={{cursor: 'pointer', userSelect: 'none'}} onClick={() => handleSort('Email')}>Email {getSortIcon('Email')}</th>
                  <th style={{cursor: 'pointer', userSelect: 'none'}} onClick={() => handleSort('TamanoEmpresaDescripcion')}>Tamaño {getSortIcon('TamanoEmpresaDescripcion')}</th>
                  <th style={{minWidth: '150px', cursor: 'pointer', userSelect: 'none'}} onClick={() => handleSort('ESTADO')}>Estado {getSortIcon('ESTADO')}</th>
                  <th>Detalle Cliente</th>
                </tr>
              </thead>
              <tbody>
                {currentData.map((lead, index) => (
                  <tr key={index}>
                    <td>{lead.RUT_LEAD}</td>
                    <td>{lead.NombreORazonSocial}</td>
                    <td>
                      <div>{lead.EJEC_ASIG}</div>
                      {ejecutivoLineaDistinto(lead) && (
                        <div className="mt-1">
                          <span
                            className="inline-block text-xs px-1.5 py-0.5 rounded bg-amber-100 text-amber-800"
                            title="Ejecutivo que tiene la línea en el sistema factoring"
                          >
                            línea: {ejecutivoLineaDistinto(lead)}
                          </span>
                        </div>
                      )}
                    </td>
                    <td>{lead.EQUIPO}</td>
                    <td>{lead.ORIGEN}</td>
                    <td>{formatDate(lead.FECHA_ASIG)}</td>
                    <td>{lead.Telefonos || '-'}</td>
                    <td>{lead.Email || '-'}</td>
                    <td>{lead.TamanoEmpresaDescripcion}</td>
                    <td>
                      <select
                        value={lead.ESTADO || ''}
                        onChange={(e) => handleEstadoChange(lead.RUT_LEAD, e.target.value)}
                        className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                      >
                        {!estadoOptions.find(opt => opt.NOM_COD_CORTO === lead.ESTADO) && lead.ESTADO && (
                          <option value={lead.ESTADO}>{lead.ESTADO}</option>
                        )}
                        {estadoOptions.map((option) => (
                          <option key={option.ID_COD} value={option.NOM_COD_CORTO}>
                            {option.NOM_COD_CORTO}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td style={{textAlign: 'center'}}>
                      <button
                        onClick={() => navigate(`/detalle-cliente/${lead.RUT_LEAD}`)}
                        className="btn-primary"
                        style={{
                          padding: '0.25rem 0.75rem',
                          fontSize: '0.75rem',
                          borderRadius: '4px',
                          width: '32px',
                          height: '32px'
                        }}
                        title="Ver detalle cliente"
                      >
                        <MdArrowForward className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        
        {/* Paginación */}
        {sortedData.length > itemsPerPage && (
          <div className="flex justify-between items-center mt-4">
            <div className="text-sm" style={{color: 'var(--if-text-muted)'}}>
              Mostrando {startIndex + 1} a {Math.min(endIndex, sortedData.length)} de {sortedData.length} registros
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-3 py-1 text-sm border rounded disabled:opacity-50 disabled:cursor-not-allowed"
                style={{borderColor: 'var(--if-gray-300)', color: 'var(--if-text-primary)'}}
              >
                Anterior
              </button>
              {getPageNumbers().map(page => (
                <button
                  key={page}
                  onClick={() => goToPage(page)}
                  className={`px-3 py-1 text-sm border rounded ${
                    currentPage === page ? 'bg-blue-500 text-white border-blue-500' : 'border-gray-300'
                  }`}
                >
                  {page}
                </button>
              ))}
              <button
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-3 py-1 text-sm border rounded disabled:opacity-50 disabled:cursor-not-allowed"
                style={{borderColor: 'var(--if-gray-300)', color: 'var(--if-text-primary)'}}
              >
                Siguiente
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EnGestion;